// BOARD-ORDER: the shared order of Work (the founder's ClickUp/Asana/Linear-grade arrangement).
// Spaces and boards carry a `position` (lower first, ties by name); a move puts one row before
// another (`before_*_id`, null: last) and the server renumbers the target's rows 0..n-1. These
// rules are pure, so the optimistic cache write (board-writes.ts) lands exactly where the server
// will put the row, and they are unit-tested beside the other Work rules.

type Ordered = { id: string; name: string; position: number };
type OrderedBoard = Ordered & { space_id: string; archived_at: string | null };
type OrderedSpace = Ordered & { kind: string; archived_at: string | null };

/** Lower position first; ties by name, then id (the API's order). */
export const byOrder = (a: Ordered, b: Ordered) =>
  a.position - b.position || a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

/** A space's live boards in order. */
export function boardsOf<B extends OrderedBoard>(boards: readonly B[], spaceId: string): B[] {
  return boards.filter((b) => b.space_id === spaceId && !b.archived_at).sort(byOrder);
}

/** The spaces in the company's order (personal ones last, as the API keeps them out of it). */
export function orderedSpaces<S extends OrderedSpace>(spaces: readonly S[]): S[] {
  return [...spaces].sort(
    (a, b) => Number(a.kind === "personal") - Number(b.kind === "personal") || byOrder(a, b),
  );
}

/**
 * The boards after `id` moved before `beforeId` (null: last) in `spaceId`: the target's live
 * boards renumbered 0..n-1, as `boards.move` does. Other rows keep their positions.
 */
export function moveBoardIn<B extends OrderedBoard>(
  boards: readonly B[],
  id: string,
  spaceId: string,
  beforeId: string | null,
): B[] {
  const moving = boards.find((b) => b.id === id);
  if (!moving) return [...boards];
  const order = boardsOf(boards, spaceId).filter((b) => b.id !== id);
  const at = beforeId === null ? -1 : order.findIndex((b) => b.id === beforeId);
  order.splice(at < 0 ? order.length : at, 0, { ...moving, space_id: spaceId });
  const placed = new Map(order.map((b, i) => [b.id, { ...b, position: i }] as const));
  return boards.map((b) => placed.get(b.id) ?? b);
}

/** The spaces after `id` moved before `beforeId` (null: last) among the live shared spaces. */
export function moveSpaceIn<S extends OrderedSpace>(
  spaces: readonly S[],
  id: string,
  beforeId: string | null,
): S[] {
  const moving = spaces.find((s) => s.id === id);
  if (!moving) return [...spaces];
  const order = spaces
    .filter((s) => s.kind !== "personal" && !s.archived_at && s.id !== id)
    .sort(byOrder);
  const at = beforeId === null ? -1 : order.findIndex((s) => s.id === beforeId);
  order.splice(at < 0 ? order.length : at, 0, moving);
  const placed = new Map(order.map((s, i) => [s.id, { ...s, position: i }] as const));
  return spaces.map((s) => placed.get(s.id) ?? s);
}

/**
 * "Move up" and "Move down" as moves: up is before the previous row, down before the row after
 * next (null: last). `undefined` where the row cannot go (first or last already).
 */
export function stepTargets(
  ids: readonly string[],
  id: string,
): { up: string | undefined; down: string | null | undefined } {
  const i = ids.indexOf(id);
  if (i < 0) return { up: undefined, down: undefined };
  return {
    up: i > 0 ? ids[i - 1] : undefined,
    down: i < ids.length - 1 ? (ids[i + 2] ?? null) : undefined,
  };
}
